import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { Grid } from '@mui/system';
import { useFormikContext } from 'formik';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { Chip } from 'src/components/Chip';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { StickyCard } from 'src/components/StickyCard';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { getUserDisplayName } from 'src/models/user/utils';
import { FormCheckbox } from 'src/shared/Forms/Fields/FormCheckbox';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { AssessmentsSequencesSelect } from 'src/shared/Forms/Selects/AssessmentsSequencesSelect';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';
import { FormGroupSelect } from 'src/shared/Forms/Selects/GroupSelect';
import { ResourceSelect } from 'src/shared/Forms/Selects/ResourceSelect';
import { FormTagsSelect } from 'src/shared/Forms/Selects/TagsSelect';
import { FormTimezoneSelect } from 'src/shared/Forms/Selects/TimezoneSelect';
import { TooltipLabel } from 'src/shared/TooltipLabel';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';

import { WeekPlanImportDialog } from '../../BatchActions/Dialogs/WeekPlanImportDialog';
import { useNewClientPage } from '../useNewClientPage';

const gridSize = {
  xs: 12,
  md: 6,
};

export const ContentInfoForm: FC<{
  setShowContentInfo: Dispatch<SetStateAction<boolean>>;
}> = ({ setShowContentInfo }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'client.newClientPage',
  });
  const { t: tGlobal } = useTranslation('translation');
  const { session } = useSession();

  const { values, setFieldValue, setValues } = useFormikContext<any>();
  const [showPlanImportModal, setShowPlanImportModal] = useState(false);

  const {
    canViewAssessment,
    canManageClientsForTrainer,
    canManageLocations,
    canViewResource,
    canViewWorkoutPlan,
  } = usePermissionCheck();

  const shouldShowImportPlan = canViewWorkoutPlan && !session?.hideCalendars;

  const { trainers, locations } = useNewClientPage();

  const handleAddPlan = (planData: any) => {
    setFieldValue('weekPlans', [...values.weekPlans, planData]);
  };

  const handleRemovePlan = (index: number) => {
    setFieldValue(
      'weekPlans',
      values.weekPlans.filter((_: any, i: number) => i !== index)
    );
  };

  return (
    <StickyCard
      title={
        <Typography sx={{ display: 'flex', alignItems: 'center' }}>
          {t('assignOrUpdateProfile')}{' '}
          <Link
            component="button"
            sx={{ ml: 1 }}
            onClick={() => {
              setValues({
                ...values,
                sequenceId: null,
                assessmentId: null,
                assessmentReminder: null,
                requiredSequence: null,
                requiredAssessment: null,
                groupToJoin: null,
                notes: null,
                trainerId: null,
                timeZone: null,
                tags: [],
                resourceMessage: null,
                resourceGroup: [],
                welcome: null,
                locationId: null,
                assessmentValue: null,
                requiredAssessmentValue: null,
                weekPlans: [],
              });
              setShowContentInfo(false);
            }}
          >
            ({t('remove')})
          </Link>
        </Typography>
      }
    >
      <Grid container spacing={2}>
        {shouldShowImportPlan && (
          <Grid size={{ xs: 12 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon icon={faPlus} />}
              onClick={() => setShowPlanImportModal(true)}
            >
              {t('importPlanToCalendar')}
            </Button>
            {values.weekPlans.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 2, flexWrap: 'wrap' }}
              >
                <Typography variant="body2">{t('weekPlans')}:</Typography>
                {values.weekPlans.map((plan: any, index: number) => (
                  <Chip
                    key={index}
                    label={plan.plan_to_copy_name}
                    onDelete={() => handleRemovePlan(index)}
                  />
                ))}
              </Stack>
            )}
          </Grid>
        )}
        {canManageLocations && (
          <Grid size={gridSize}>
            <FormSelect
              label={t('location')}
              name="locationId"
              size="medium"
              prompt={t('selectLocationPrompt')}
              options={locations.map((location) => ({
                label: location.name,
                value: location.id as string,
              }))}
              onChange={(e) => {
                setFieldValue('locationId', e.target.value);
                const location = locations.find(
                  (location) => location.id === e.target.value
                );
                if (location && location.timeZone) {
                  setFieldValue('timeZone', location.timeZone);
                }
              }}
            />
          </Grid>
        )}
        <Grid size={gridSize}>
          <FormTimezoneSelect name="timeZone" />
        </Grid>
        {canManageClientsForTrainer && (
          <Grid size={gridSize}>
            <BaseSelect
              label={tGlobal('trainer.one')}
              value={values.trainerId}
              options={trainers.map((trainer) => ({
                id: trainer.id,
                label: getUserDisplayName(trainer) || '',
              }))}
              valueOnly
              onChange={(value) => {
                setFieldValue('trainerId', value);
              }}
              virtualized
            />
          </Grid>
        )}
        <Grid size={gridSize}>
          <FormTagsSelect
            label={t('tags')}
            tagType="client"
            name="tags"
            multiple
          />
        </Grid>

        {canViewAssessment && (
          <>
            <Grid size={gridSize}>
              <AssessmentsSequencesSelect
                name={'assessmentValue'}
                value={values.assessmentValue}
                label={
                  <TooltipLabel
                    text={t('assignAssignmentOrSequence')}
                    tooltip={t('assignAssignment')}
                  />
                }
                onChange={(item) => {
                  setFieldValue('assessmentValue', item?.id || '');
                  if (item && item.type === 'grouping') {
                    setFieldValue('sequenceId', item.id);
                    setFieldValue('assessmentId', null);
                  } else if (item && item.type === 'assessment') {
                    setFieldValue('assessmentId', item.id);
                    setFieldValue('sequenceId', null);
                  } else {
                    setFieldValue('assessmentId', null);
                    setFieldValue('sequenceId', null);
                  }
                }}
              />
              {(values.assessmentId || values.sequenceId) && (
                <FormCheckbox
                  name="assessmentReminder"
                  label={t('sendRemindersUntilCompleted')}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>
            <Grid size={gridSize}>
              <AssessmentsSequencesSelect
                name={'requiredAssessmentValue'}
                value={values.requiredAssessmentValue}
                label={
                  <TooltipLabel
                    text={t('requiredAssignmentOrSequence')}
                    tooltip={t('requiredAssignmentOrSequenceDesc')}
                  />
                }
                onChange={(item) => {
                  setFieldValue('requiredAssessmentValue', item?.id || '');
                  if (item && item.type === 'grouping') {
                    setFieldValue('requiredSequence', item.id);
                    setFieldValue('requiredAssessment', null);
                  } else if (item && item.type === 'assessment') {
                    setFieldValue('requiredAssessment', item.id);
                    setFieldValue('requiredSequence', null);
                  } else {
                    setFieldValue('requiredAssessment', null);
                    setFieldValue('requiredSequence', null);
                  }
                }}
              />
            </Grid>
          </>
        )}

        <Grid size={gridSize}>
          <FormTextField
            label={t('welcomeMessage')}
            name="welcome"
            multiline
            rows={2}
            placeholder={t('welcomeMessagePlaceholder', {
              client: tGlobal('trainer.client.one').toLowerCase(),
            })}
          />
        </Grid>
        <Grid size={gridSize}>
          <FormTextField
            label={t('notes')}
            name="notes"
            multiline
            rows={2}
            placeholder={t('notesPlaceholder')}
          />
        </Grid>
        {canViewResource && (
          <Grid size={gridSize}>
            <ResourceSelect
              label={t('assignAResource')}
              value={values.resourceGroup?.[0]?.['id'] || ''}
              blurOnSelect
              valueOnly={false}
              onChange={(value) => {
                setFieldValue('resourceGroup', [
                  ...values.resourceGroup,
                  value,
                ]);
              }}
            />
            <Stack direction="row" gap={1} sx={{ mt: 1 }}>
              {values.resourceGroup?.map((resource: any) => (
                <Chip
                  key={resource.id}
                  label={resource.label}
                  onDelete={() => {
                    setFieldValue('resourceGroup', [
                      ...values.resourceGroup.filter(
                        (r: any) => r.id !== resource.id
                      ),
                    ]);
                  }}
                />
              ))}
            </Stack>
            {values.resourceGroup?.length > 0 && (
              <FormTextField
                label={t('resourceMessage')}
                name="resourceMessage"
                multiline
                rows={2}
                sx={{ mt: 1 }}
                placeholder={t('resourceMessagePlaceholder', {
                  client: tGlobal('trainer.client.one').toLowerCase(),
                })}
              />
            )}
          </Grid>
        )}
        <Grid size={gridSize}>
          <FormGroupSelect label={t('group')} valueOnly name="groupToJoin" />
        </Grid>
      </Grid>
      <WeekPlanImportDialog
        key={showPlanImportModal ? 'open' : 'closed'}
        open={showPlanImportModal}
        onClose={() => setShowPlanImportModal(false)}
        onAddPlan={handleAddPlan}
      />
    </StickyCard>
  );
};
